/**
 * useComparisonData - Fetches comparison data for Performance Analytics
 *
 * Makes backend calls based on selected comparison type (Position, League,
 * Watchlist, or Individual Player) and calculates averages.
 * Supports both standard metrics (Carrying, Dribbling, etc.) and custom
 * organization-specific metrics from `bioData.customStats`.
 * @module features/player-detail/hooks/useComparisonData
 */

import { useMemo } from "react";

import {
  PerformanceAnalyticsPlayerScoresFragment,
  usePerformanceAnalyticsFilterPlayersQuery,
  usePerformanceAnalyticsGetComparisonPlayerQuery,
  useWatchlistListWatchlistPlayersQuery,
} from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

import type { BackendCustomStats } from "@/types/customMetrics";

import { ALL_CATEGORICAL_METRICS } from "../metricDefinitions";
import type { CompareItem, ComparisonDataStatus } from "../types";
import {
  buildCategoricalScores,
  extractCustomMetricScores,
  safeRound,
  withGlobalScores,
} from "../utils/performanceAnalyticsUtils";

/**
 * Read a numeric score field from a bioData / season object, coercing missing
 * or non-numeric values to null. Lets the metric configs below address fields
 * by name from the shared {@link ALL_CATEGORICAL_METRICS} definitions.
 * @param source - Object holding the score fields (bioData or a season entry)
 * @param field - Field name to read from the source
 * @returns The numeric value, or null when missing/non-numeric
 */
const readNumericField = (
  source: Record<string, unknown> | null | undefined,
  field: string
): number | null => {
  const raw = source?.[field];
  return typeof raw === "number" ? raw : null;
};

/** Default number of players to fetch for comparison (300 for better distribution density) */
const COMPARISON_PLAYER_LIMIT = 300;

/**
 * Comparison data result from hook
 */
interface ComparisonDataResult {
  /** Calculated average scores for the comparison group, null values indicate missing data */
  readonly averages: Record<string, number | null> | null;
  /** Array of comparison players with scores */
  readonly players: readonly PerformanceAnalyticsPlayerScoresFragment[];
  /** Whether data is currently loading */
  readonly loading: boolean;
  /** Error if query failed */
  readonly error: Error | null;
  /** Lifecycle of the active category query - see {@link ComparisonDataStatus} */
  readonly status: ComparisonDataStatus;
}

/**
 * Resolve the lifecycle status of the active comparison query.
 *
 * Data wins over an error: `cache-and-network` can report a failed refresh
 * while still holding an earlier answer, and that answer is still worth
 * drawing. With neither data nor error the request is pending, whether Apollo
 * reports it as loading, is holding it for team selection, or has not
 * dispatched it yet on the first render after its variables settle.
 * @param params - Inputs describing the active query
 * @param params.hasCompareItem - Whether there is a comparison item to query for
 * @param params.error - Apollo error of the active query
 * @param params.hasData - Whether the active query has returned data
 * @returns The comparison data status
 */
export const resolveComparisonDataStatus = ({
  hasCompareItem,
  error,
  hasData,
}: {
  readonly hasCompareItem: boolean;
  readonly error: Error | null | undefined;
  readonly hasData: boolean;
}): ComparisonDataStatus => {
  if (!hasCompareItem) return "idle";
  if (hasData) return "ready";
  if (error) return "error";
  return "loading";
};

/** Metric configuration for score calculation */
interface MetricConfig {
  readonly key: string;
  readonly getter: (
    bioData: PerformanceAnalyticsPlayerScoresFragment["bioData"]
  ) => number | null | undefined;
}

/**
 * Global-only metrics with no seasonal variant.
 * Physical Score and GPR are always sourced from bioData regardless of season selection.
 */
const GLOBAL_ONLY_METRIC_CONFIGS: readonly MetricConfig[] = [
  { key: "Physical Score", getter: b => b?.physicalScore },
  { key: "GPR", getter: b => b?.gpr },
] as const;

/**
 * Categorical metric configurations for global score extraction, derived from
 * the shared {@link ALL_CATEGORICAL_METRICS} definitions (SE-4168).
 */
const CATEGORICAL_GLOBAL_METRIC_CONFIGS: readonly MetricConfig[] =
  ALL_CATEGORICAL_METRICS.map(metric => ({
    key: metric.label,
    getter: b => readNumericField(b, metric.globalField),
  }));

/** Metric configurations for score extraction (global scores, including global-only metrics) */
const METRIC_CONFIGS: readonly MetricConfig[] = [
  ...CATEGORICAL_GLOBAL_METRIC_CONFIGS,
  ...GLOBAL_ONLY_METRIC_CONFIGS,
];

/** Metric configuration for seasonal score extraction */
interface SeasonalMetricConfig {
  readonly key: string;
  readonly getter: (
    seasonData: NonNullable<
      PerformanceAnalyticsPlayerScoresFragment["seasonCategoricalScores"]
    >[number]
  ) => number | null | undefined;
}

/**
 * Metric configurations for seasonal score extraction, derived from the shared
 * {@link ALL_CATEGORICAL_METRICS} definitions (SE-4168). Physical Score and GPR
 * are global-only and accumulated separately via {@link GLOBAL_ONLY_METRIC_CONFIGS}.
 */
const SEASONAL_METRIC_CONFIGS: readonly SeasonalMetricConfig[] =
  ALL_CATEGORICAL_METRICS.map(metric => ({
    key: metric.label,
    getter: s => readNumericField(s, metric.positionalField),
  }));

/** Accumulator type for score calculation */
interface ScoreAccumulator {
  readonly [key: string]: { readonly sum: number; readonly count: number };
}

/**
 * Accumulate a single metric value into the score accumulator
 * @param acc - Current accumulator state
 * @param key - Metric key name
 * @param value - Score value to accumulate (null/undefined are skipped)
 * @returns Updated accumulator with the value added to the metric's sum and count
 */
const accumulateMetric = (
  acc: ScoreAccumulator,
  key: string,
  value: number | null | undefined
): ScoreAccumulator => {
  if (value == null) return acc;

  const current = acc[key] ?? { sum: 0, count: 0 };
  return {
    ...acc,
    [key]: { sum: current.sum + value, count: current.count + 1 },
  };
};

/**
 * Accumulate custom organization metrics from bioData.customStats into the score accumulator
 * @param acc - Current accumulator state
 * @param customStats - Backend custom stats JSONB or null/undefined
 * @returns Updated accumulator with custom metric values added
 */
const accumulateCustomMetrics = (
  acc: ScoreAccumulator,
  customStats: BackendCustomStats | null | undefined
): ScoreAccumulator => {
  const customScores = extractCustomMetricScores(customStats);
  return Object.entries(customScores).reduce<ScoreAccumulator>(
    (innerAcc, [key, value]) => accumulateMetric(innerAcc, key, value),
    acc
  );
};

/**
 * Minimal player shape of a watchlist player node. The
 * categorical score fields are read by name from {@link ALL_CATEGORICAL_METRICS}
 * in {@link mapToComparisonPlayer}, so they are intentionally not enumerated
 * here — only the non-score fields the mapper reads directly are typed.
 */
interface PlayerLikeNode {
  readonly id: string;
  readonly name: string;
  readonly knownName?: string | null;
  readonly firstName: string;
  readonly lastName: string;
  readonly bioData?: {
    readonly ggPlayerKey: string;
    readonly physicalScore?: number | null;
    readonly gpr?: number | null;
    readonly customStats?: Record<string, unknown> | null;
  } | null;
  readonly seasonCategoricalScores?: PerformanceAnalyticsPlayerScoresFragment["seasonCategoricalScores"];
}

/**
 * Map a watchlist player node to the
 * PerformanceAnalyticsPlayerScoresFragment shape, projecting every categorical
 * metric's global field from the shared definitions.
 * @param node - Player-like node from the watchlist query
 * @returns Mapped player matching the comparison fragment shape
 */
const mapToComparisonPlayer = (
  node: PlayerLikeNode
): PerformanceAnalyticsPlayerScoresFragment => {
  const bio = node.bioData;
  return {
    id: node.id,
    name: node.name,
    knownName: node.knownName,
    firstName: node.firstName,
    lastName: node.lastName,
    bioData: bio
      ? ({
          ggPlayerKey: bio.ggPlayerKey,
          ...Object.fromEntries(
            ALL_CATEGORICAL_METRICS.map(metric => [
              metric.globalField,
              readNumericField(
                bio as Record<string, unknown>,
                metric.globalField
              ),
            ])
          ),
          physicalScore: bio.physicalScore,
          gpr: bio.gpr,
          customStats: bio.customStats,
        } as PerformanceAnalyticsPlayerScoresFragment["bioData"])
      : null,
    seasonCategoricalScores: node.seasonCategoricalScores ?? [],
  };
};

/**
 * Calculate average scores from an array of players using immutable reduce
 * @param players - Array of players with score data
 * @param seasonKey - Optional season key (e.g., "2024") for season-specific scores
 * @returns Average scores for each metric, including custom organization metrics
 * @remarks Custom metrics from `bioData.customStats` are dynamically accumulated
 * alongside standard metrics. Players without custom stats are simply skipped
 * for those metrics (count stays at 0, so the average is calculated only from
 * players that have the metric).
 */
const calculateAverageScores = (
  players: readonly PerformanceAnalyticsPlayerScoresFragment[],
  seasonKey?: string | null
): Record<string, number | null> => {
  if (players.length === 0) {
    return {};
  }

  // Determine if we should use seasonal scores
  const useSeasonal = Boolean(seasonKey);

  // Accumulate sums and counts immutably
  const totals = players.reduce<ScoreAccumulator>(
    (acc, player) => {
      // Try seasonal scores first if requested
      if (useSeasonal) {
        const seasonData = player.seasonCategoricalScores?.find(
          s => String(s.ggUnifiedSeasonKey) === seasonKey
        );

        if (seasonData) {
          const seasonalAcc = SEASONAL_METRIC_CONFIGS.reduce<ScoreAccumulator>(
            (innerAcc, config) =>
              accumulateMetric(innerAcc, config.key, config.getter(seasonData)),
            acc
          );

          // Physical Score and GPR are global-only — accumulate from bioData
          const globalAcc = GLOBAL_ONLY_METRIC_CONFIGS.reduce<ScoreAccumulator>(
            (innerAcc, config) =>
              accumulateMetric(
                innerAcc,
                config.key,
                config.getter(player.bioData)
              ),
            seasonalAcc
          );

          return accumulateCustomMetrics(
            globalAcc,
            player.bioData?.customStats as BackendCustomStats | null | undefined
          );
        }
      }

      // Fallback to global scores
      const bioData = player.bioData;
      if (!bioData) return acc;

      const standardAcc = METRIC_CONFIGS.reduce<ScoreAccumulator>(
        (innerAcc, config) =>
          accumulateMetric(innerAcc, config.key, config.getter(bioData)),
        acc
      );

      return accumulateCustomMetrics(
        standardAcc,
        bioData.customStats as BackendCustomStats | null | undefined
      );
    },
    METRIC_CONFIGS.reduce<ScoreAccumulator>(
      (acc, config) => ({
        ...acc,
        [config.key]: { sum: 0, count: 0 },
      }),
      {}
    )
  );

  // Convert totals to averages, null when no data available for a metric
  return Object.entries(totals).reduce(
    (acc, [key, { sum, count }]) => ({
      ...acc,
      [key]: count > 0 ? Math.round(sum / count) : null,
    }),
    {} as Record<string, number | null>
  );
};

/**
 * Extract scores from a single player for individual comparison
 * @param player - Single player with score data
 * @param seasonKey - Optional season key (e.g., "2024") for season-specific scores
 * @returns Player's scores including standard and custom organization metrics
 * @remarks Custom metrics are extracted once and spread into both seasonal and
 * global return paths, since custom stats have no seasonal variant.
 */
const extractSinglePlayerScores = (
  player: PerformanceAnalyticsPlayerScoresFragment | null | undefined,
  seasonKey?: string | null
): Record<string, number | null> => {
  if (!player?.bioData) {
    return {};
  }

  const bioData = player.bioData;

  // Custom organization metrics from bioData.customStats
  const customScores = extractCustomMetricScores(
    bioData.customStats as BackendCustomStats | null | undefined
  );

  // If season is specified, try to find seasonal scores
  const seasonData = seasonKey
    ? player.seasonCategoricalScores?.find(
        s => String(s.ggUnifiedSeasonKey) === seasonKey
      )
    : undefined;

  const categorical = seasonData
    ? buildCategoricalScores(ALL_CATEGORICAL_METRICS, seasonData, "positional")
    : buildCategoricalScores(ALL_CATEGORICAL_METRICS, bioData, "global");

  // Physical Score and GPR are global-only regardless of season selection.
  return withGlobalScores(
    categorical,
    safeRound(bioData.physicalScore),
    safeRound(bioData.gpr),
    customScores
  );
};

/**
 * Build comparison result from a list of already-extracted players
 * @param players - Extracted comparison players
 * @param seasonKey - Optional season key for score calculation
 * @returns Object with players array and calculated averages (null when empty)
 */
const buildComparisonResult = (
  players: readonly PerformanceAnalyticsPlayerScoresFragment[],
  seasonKey?: string | null
) => ({
  players,
  averages:
    players.length > 0 ? calculateAverageScores(players, seasonKey) : null,
});

/**
 * Hook to fetch comparison data based on selected comparison type
 * @param categoryType - Type of comparison (position, leagues, watch_list, players)
 * @param compareItem - Selected comparison item with id and name
 * @param currentPlayerId - Current player's ID (to exclude from comparison)
 * @param seasonKey - Optional season key (e.g., "2024") for season-specific scores
 * @param mainPlayerLeagueId - Optional league ID of the main player (used to filter position comparison by same league)
 * @returns Comparison data including averages, players, loading state, and error
 */
export const useComparisonData = (
  categoryType: string,
  compareItem: CompareItem | null,
  currentPlayerId: string,
  seasonKey?: string | null,
  mainPlayerLeagueId?: string | null
): ComparisonDataResult => {
  const compareItemId = compareItem?.id ?? "";
  // Anchor comparison-player scores to the active team so league/position
  // averages reflect the same translation context the profile screen uses.
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  // Position comparison: filterPlayers with positionIds AND leagueIds
  // NOTE: League filter is ALWAYS applied when mainPlayerLeagueId is available.
  // This ensures comparison players are from the same league as the main player,
  // providing more relevant position-based comparisons within the same competition level.
  const {
    data: positionData,
    loading: positionLoading,
    error: positionError,
  } = usePerformanceAnalyticsFilterPlayersQuery({
    variables: {
      filter: {
        positionIds: [compareItemId],
        // League filter always applied - compares against same league for relevance
        ...(mainPlayerLeagueId ? { leagueIds: [mainPlayerLeagueId] } : {}),
      },
      first: COMPARISON_PLAYER_LIMIT,
    },
    skip:
      categoryType !== "position" || !compareItemId || !isTeamSelectionLoaded,
    fetchPolicy: "cache-and-network",
  });

  // League comparison: filterPlayers with leagueIds
  const {
    data: leagueData,
    loading: leagueLoading,
    error: leagueError,
  } = usePerformanceAnalyticsFilterPlayersQuery({
    variables: {
      filter: { leagueIds: [compareItemId] },
      first: COMPARISON_PLAYER_LIMIT,
    },
    skip:
      categoryType !== "leagues" || !compareItemId || !isTeamSelectionLoaded,
    fetchPolicy: "cache-and-network",
  });

  // Watchlist comparison: listWatchlistPlayers
  const {
    data: watchlistData,
    loading: watchlistLoading,
    error: watchlistError,
  } = useWatchlistListWatchlistPlayersQuery({
    variables: {
      watchlistId: compareItemId,
      first: COMPARISON_PLAYER_LIMIT,
    },
    skip: categoryType !== "watch_list" || !compareItemId,
    fetchPolicy: "cache-and-network",
  });

  // Individual player comparison: getPlayer
  const {
    data: playerData,
    loading: playerLoading,
    error: playerError,
  } = usePerformanceAnalyticsGetComparisonPlayerQuery({
    variables: { id: compareItemId },
    skip:
      categoryType !== "players" || !compareItemId || !isTeamSelectionLoaded,
    fetchPolicy: "cache-and-network",
  });

  // Determine loading state
  const loading =
    positionLoading || leagueLoading || watchlistLoading || playerLoading;

  // Determine error
  const error =
    positionError || leagueError || watchlistError || playerError || null;

  // Lifecycle of the query this category actually uses (SE-8283). Keyed by
  // category so an unknown category resolves to idle rather than pending.
  // `result` is the category own root field. The client runs with
  // `errorPolicy: "all"`, so a failed request comes back as `data: null` (or
  // a null root field) WITH its error; "data is not undefined" read that
  // failure as an empty answer. Only a present root field, or a response
  // without an error, counts as an answer.
  const activeQuery: Readonly<
    Record<
      string,
      {
        readonly data: unknown;
        readonly result: unknown;
        readonly error?: Error;
      }
    >
  > = {
    position: {
      data: positionData,
      result: positionData?.filterPlayers,
      error: positionError,
    },
    leagues: {
      data: leagueData,
      result: leagueData?.filterPlayers,
      error: leagueError,
    },
    watch_list: {
      data: watchlistData,
      result: watchlistData?.listWatchlistPlayers,
      error: watchlistError,
    },
    players: {
      data: playerData,
      result: playerData?.getPlayer,
      error: playerError,
    },
  };
  const active = activeQuery[categoryType];
  const status = resolveComparisonDataStatus({
    hasCompareItem: Boolean(compareItemId) && active !== undefined,
    error: active?.error,
    hasData: active?.result != null || (active?.data != null && !active?.error),
  });

  // Extract players and calculate averages
  const { players, averages } = useMemo(() => {
    // Individual player comparison - return single player's scores directly
    if (categoryType === "players" && playerData?.getPlayer) {
      const singlePlayerScores = extractSinglePlayerScores(
        playerData.getPlayer,
        seasonKey
      );
      return {
        players: [playerData.getPlayer],
        averages:
          Object.keys(singlePlayerScores).length > 0
            ? singlePlayerScores
            : null,
      };
    }

    // Position comparison
    if (categoryType === "position" && positionData?.filterPlayers?.edges) {
      const extractedPlayers = positionData.filterPlayers.edges
        .map(edge => edge.node)
        .filter(
          (player): player is PerformanceAnalyticsPlayerScoresFragment =>
            player != null && player.id !== currentPlayerId
        );
      return buildComparisonResult(extractedPlayers, seasonKey);
    }

    // League comparison
    if (categoryType === "leagues" && leagueData?.filterPlayers?.edges) {
      const extractedPlayers = leagueData.filterPlayers.edges
        .map(edge => edge.node)
        .filter(
          (player): player is PerformanceAnalyticsPlayerScoresFragment =>
            player != null && player.id !== currentPlayerId
        );
      return buildComparisonResult(extractedPlayers, seasonKey);
    }

    // Watchlist comparison - need to extract from WatchlistPlayer type
    if (
      categoryType === "watch_list" &&
      watchlistData?.listWatchlistPlayers?.edges
    ) {
      const extractedPlayers = watchlistData.listWatchlistPlayers.edges
        .map(edge => edge.node)
        .filter(node => node != null && node.id !== currentPlayerId)
        .map(mapToComparisonPlayer);
      return buildComparisonResult(extractedPlayers, seasonKey);
    }

    // No matching comparison type or no data
    return {
      players: [] as readonly PerformanceAnalyticsPlayerScoresFragment[],
      averages: null,
    };
  }, [
    categoryType,
    currentPlayerId,
    positionData,
    leagueData,
    watchlistData,
    playerData,
    seasonKey,
  ]);

  return {
    averages,
    players,
    loading,
    error: error ?? null,
    status,
  };
};
